/* global JSX */
import React, { useState, useEffect } from 'react';
import {
  SharedInstanceId,
  ClientId,
  ComponentFactory,
  useRegisterRecycledComponent,
} from 'app/components/utilities/RecycledComponentsProvider/RecycledComponentsProvider';
import { useCallbackOne } from 'app/utils/useMemoOne';
import { createGuid } from 'app/types/guid';

interface IProps {
  sharedInstanceId: SharedInstanceId;
  componentFactory: ComponentFactory;
}

const RecycledComponent: React.FC<IProps> = ({
  sharedInstanceId,
  componentFactory,
}) => {
  const [clientId] = useState<ClientId>(() => createGuid());

  const {
    register,
    unregister,
    getTarget,
    updateComponentFactory,
  } = useRegisterRecycledComponent();

  const target = getTarget(
    sharedInstanceId,
    clientId,
  );

  useEffect(() => {
    register(
      sharedInstanceId,
      clientId,
    );

    return () => {
      unregister(sharedInstanceId, clientId);
    };
  }, [register, unregister, clientId, sharedInstanceId]);

  useEffect(() => {
    updateComponentFactory(sharedInstanceId, clientId, componentFactory);
  }, [
    updateComponentFactory,
    componentFactory,
    sharedInstanceId,
    clientId,
  ]);

  const handleRef = useCallbackOne((ref: HTMLElement | null) => {
    if (!ref) return;

    target && ref.appendChild(target);
  }, [target]);

  return (
    <div ref={handleRef} />
  );
};

export const createRecycledComponent = <
  T extends React.ComponentType<
    any
  > | keyof JSX.IntrinsicElements,
  Props extends React.ComponentProps<T>,
>(
  Component: T,
  getSharedInstanceId: (props: Props) => SharedInstanceId,
) => React.memo((props: Props) => (
  <RecycledComponent
    sharedInstanceId={getSharedInstanceId(props)}
    componentFactory={() => <Component {...props} />}
  />
));
